// @tennis-matches/services/feature-flags
// Migrated from src/services/feature-flags/feature-flags.remote.js

import { getSupabaseClient } from "../supabase/index.js";
import { resolveAdminApiHeaders } from "../api/api-auth-headers.js";

// Cache for feature flags (key -> enabled)
let __flagsCache = null;
let __flagsCacheTimestamp = null;
const CACHE_TTL_MS = 60000; // 1 minute cache
const STORAGE_KEY = "tt_feature_flags_cache_v1";

function __setCache(flags) {
  const map = flags instanceof Map ? flags : new Map();
  __flagsCache = map;
  __flagsCacheTimestamp = Date.now();

  // Persist for next boot to avoid UI "flash"
  try {
    const obj = Object.fromEntries(map.entries());
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ts: __flagsCacheTimestamp, flags: obj }),
    );
  } catch (_) {}
}

function __hydrateCacheFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts);
    const flagsObj =
      parsed?.flags && typeof parsed.flags === "object" ? parsed.flags : null;
    if (!Number.isFinite(ts) || !flagsObj) return;

    const map = new Map();
    for (const [k, v] of Object.entries(flagsObj)) {
      if (!k) continue;
      map.set(String(k), Boolean(v));
    }

    __flagsCache = map;
    __flagsCacheTimestamp = ts;
  } catch (_) {}
}

// Synchronously hydrate cache (prevents first-load flashing)
__hydrateCacheFromStorage();

export function areFeatureFlagsReadySync() {
  return !!(__flagsCache && __flagsCacheTimestamp);
}

/** Prefixe, die ohne explizites Flag default OFF sind (Kill-Switch / Pilot). */
export const OPT_IN_FEATURE_PREFIXES = Object.freeze([
  "assistant.",
  "billing.",
]);

/** Bekannte Opt-in-Keys (Dokumentation + Tests). */
export const OPT_IN_FEATURE_KEYS = Object.freeze([
  "assistant.trainer_briefing",
  "assistant.trainer_digest",
  "assistant.autopilot.outbound",
  "billing.stripe",
  "billing.paypal",
]);

/**
 * @param {string} featureKey
 * @returns {boolean}
 */
export function isOptInFeatureKey(featureKey) {
  const key = String(featureKey || "");
  if (!key) return false;
  return OPT_IN_FEATURE_PREFIXES.some((p) => key.startsWith(p));
}

/**
 * Opt-in: ohne Cache / ohne Eintrag → false (kein stilles Enable).
 * @param {string} featureKey
 * @returns {boolean}
 */
export function isOptInFeatureEnabledSync(featureKey) {
  if (!featureKey || typeof featureKey !== "string") return false;
  if (!isOptInFeatureKey(featureKey)) {
    return isFeatureEnabledSync(featureKey);
  }
  if (!__flagsCache || !__flagsCacheTimestamp) return false;
  if (!__flagsCache.has(featureKey)) return false;
  return Boolean(__flagsCache.get(featureKey));
}

/**
 * @param {Record<string, boolean>} flagsObj
 */
export function __primeFeatureFlagsCacheForTests(flagsObj) {
  const map = new Map();
  if (flagsObj && typeof flagsObj === "object") {
    for (const [k, v] of Object.entries(flagsObj)) {
      if (!k) continue;
      map.set(String(k), Boolean(v));
    }
  }
  __flagsCache = map;
  __flagsCacheTimestamp = Date.now();
}

/**
 * Synchronously checks if a feature is enabled (uses cache)
 * @param {string} featureKey - Feature key to check
 * @returns {boolean} True if enabled, false otherwise (defaults to true if not found or not cached)
 */
export function isFeatureEnabledSync(featureKey) {
  if (!featureKey || typeof featureKey !== "string") {
    return true; // Default: enabled if invalid key
  }

  if (isOptInFeatureKey(featureKey)) {
    return isOptInFeatureEnabledSync(featureKey);
  }

  // Check cache
  if (!__flagsCache || !__flagsCacheTimestamp) {
    return true; // Default: enabled if not cached
  }

  const age = Date.now() - __flagsCacheTimestamp;
  if (age >= CACHE_TTL_MS) {
    // Keep using stale cache instead of enabling everything.
    // A background refresh (loadFeatureFlags) may update the cache later.
  }

  // If flag exists, return its value; otherwise default to true
  return __flagsCache.has(featureKey) ? __flagsCache.get(featureKey) : true;
}

/**
 * Loads all feature flags from Supabase
 * @returns {Promise<{ok: boolean, flags?: Map<string, boolean>, reason?: string}>}
 */
export async function loadFeatureFlags() {
  // Check cache first
  if (__flagsCache && __flagsCacheTimestamp) {
    const age = Date.now() - __flagsCacheTimestamp;
    if (age < CACHE_TTL_MS) {
      return {
        ok: true,
        flags:
          __flagsCache instanceof Map ? new Map(__flagsCache) : __flagsCache,
      };
    }
  }

  // Read via Supabase (RLS: SELECT for everyone). Admin-API nur für Schreiben / Superadmin-UI.
  const supabase = await getSupabaseClient();
  if (!supabase) {
    // Fallback: all features enabled if no Supabase
    console.warn("[feature-flags] No Supabase client, all features enabled");
    __setCache(new Map());
    return { ok: true, flags: new Map() };
  }

  try {
    const { data, error } = await supabase
      .from("feature_flags")
      .select("feature_key, enabled");

    if (error) {
      const errorCode = String(error?.code || "");
      if (errorCode === "42P01" || errorCode === "42703") {
        // Table doesn't exist yet - all features enabled
        console.log("[feature-flags] Table not found, all features enabled");
        __setCache(new Map());
        return { ok: true, flags: new Map() };
      }
      console.warn("[feature-flags] Failed to load flags:", error);
      __setCache(new Map());
      return { ok: true, flags: new Map() }; // Graceful fallback
    }

    const flags = new Map();
    if (Array.isArray(data)) {
      for (const row of data) {
        if (row?.feature_key) {
          flags.set(String(row.feature_key), Boolean(row.enabled));
        }
      }
    }

    // Update cache + persist
    __setCache(flags);

    return { ok: true, flags };
  } catch (e) {
    console.warn("[feature-flags] Error loading flags:", e);
    __setCache(new Map());
    return { ok: true, flags: new Map() }; // Graceful fallback
  }
}

/**
 * Checks if a feature is enabled
 * @param {string} featureKey - Feature key to check
 * @returns {Promise<boolean>} True if enabled, false otherwise (defaults to true if not found)
 */
export async function isFeatureEnabled(featureKey) {
  if (!featureKey || typeof featureKey !== "string") {
    return true; // Default: enabled if invalid key
  }

  const result = await loadFeatureFlags();
  if (!result.ok || !result.flags) {
    return true; // Default: enabled if error
  }

  // If flag exists, return its value; otherwise default to true
  return result.flags.has(featureKey) ? result.flags.get(featureKey) : true;
}

async function __updateFeatureFlagViaSupabase(featureKey, enabled) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  try {
    const { data: isSa, error: saErr } = await supabase.rpc("is_superadmin");
    if (saErr || isSa !== true) return { ok: false, reason: "not-authorized" };

    const now = new Date().toISOString();
    const { error } = await supabase.from("feature_flags").upsert(
      {
        feature_key: featureKey,
        enabled: Boolean(enabled),
        updated_at: now,
      },
      { onConflict: "feature_key" },
    );
    if (error) {
      return {
        ok: false,
        reason: String(error?.message || error?.code || "db-error"),
      };
    }

    __flagsCache = null;
    __flagsCacheTimestamp = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}

    return { ok: true };
  } catch (e) {
    return { ok: false, reason: String(e?.message || e) };
  }
}

function __shouldTrySupabaseFeatureFlagFallback(res, reason) {
  if (res?.status === 401) return true;
  const r = String(reason || "")
    .trim()
    .toLowerCase();
  if (!r) return false;
  return (
    r === "unauthorized" ||
    r === "missing-config" ||
    r === "missing-supabase-service-key" ||
    r.startsWith("db-error") ||
    r.startsWith("http-5")
  );
}

/**
 * Updates a feature flag
 * @param {string} featureKey - Feature key to update
 * @param {boolean} enabled - Whether the feature should be enabled
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function updateFeatureFlag(featureKey, enabled) {
  if (!featureKey || typeof featureKey !== "string") {
    return { ok: false, reason: "invalid-key" };
  }

  // Prefer admin API (Cloudflare admin function or Express). Fallback: Supabase RLS for global superadmin.
  try {
    const headers = await resolveAdminApiHeaders();
    const res = await fetch("/api/admin/feature-flags", {
      method: "PUT",
      credentials: "include",
      headers,
      body: JSON.stringify({
        feature_key: featureKey,
        enabled: Boolean(enabled),
      }),
    });
    const ct = String(res.headers.get("content-type") || "");
    const payload = ct.includes("application/json") ? await res.json() : null;
    if (!res.ok || !payload?.ok) {
      const reason = String(
        payload?.reason ||
          payload?.detail?.message ||
          payload?.detail?.error ||
          (typeof payload?.detail === "string" ? payload.detail : "") ||
          `http-${res.status}`,
      );
      if (__shouldTrySupabaseFeatureFlagFallback(res, reason)) {
        const fallback = await __updateFeatureFlagViaSupabase(
          featureKey,
          enabled,
        );
        if (fallback.ok) return fallback;
        return {
          ok: false,
          reason: String(fallback.reason || reason || "db-error"),
        };
      }
      return { ok: false, reason };
    }

    __flagsCache = null;
    __flagsCacheTimestamp = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}

    return { ok: true };
  } catch (e) {
    const fallback = await __updateFeatureFlagViaSupabase(featureKey, enabled);
    if (fallback.ok) return fallback;
    return {
      ok: false,
      reason: "admin-api-unavailable",
      error: String(e?.message || e),
    };
  }
}

/**
 * Clears the feature flags cache (useful after updates)
 */
export function clearFeatureFlagsCache() {
  __flagsCache = null;
  __flagsCacheTimestamp = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}
